import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Users, Globe, Store, IndianRupee, TrendingUp, ShieldCheck, ArrowRight, BadgeCheck, AlarmClock, Ban, CircleSlash } from "lucide-react";
import api, { fmtINR } from "../../lib/api";
import { StatCard, PageHeader, LoadingRows } from "../../components/Shared";
import { Button } from "../../components/ui/button";

export default function RootDashboard() {
  const [s, setS] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/root/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <LoadingRows rows={5} />;

  return (
    <div data-testid="root-dashboard">
      <PageHeader title="Root Platform Overview" subtitle="Highest-level view across super admins, partners, business owners and websites.">
        <Button data-testid="root-manage-admins" onClick={() => navigate("/root/admins")} className="rounded-full font-bold">
          Manage Super Admins <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
        <StatCard icon={Crown} label="Super Admins" value={s.super_admins} testid="root-stat-admins" />
        <StatCard icon={Users} label="Partners" value={s.partners} testid="root-stat-partners" delay={0.05} />
        <StatCard icon={Store} label="Business Owners" value={s.owners} testid="root-stat-owners" delay={0.1} />
        <StatCard icon={Globe} label="Websites" value={`${s.active_websites} / ${s.websites}`} sub="active / total" testid="root-stat-websites" delay={0.15} />
        <StatCard icon={IndianRupee} label="Total Revenue" value={fmtINR(s.revenue)} sub="collected payments" testid="root-stat-revenue" delay={0.2} />
        <StatCard icon={TrendingUp} label="Partner MRR" value={fmtINR(s.partner_mrr)} sub="per-partner website billing (unchanged)" testid="root-stat-partner-mrr" delay={0.25} />
        <StatCard icon={ShieldCheck} label="Super Admin MRR" value={fmtINR(s.admin_mrr)} sub="from admin plans" testid="root-stat-admin-mrr" delay={0.3} />
        <StatCard icon={TrendingUp} label="Total MRR" value={fmtINR(s.total_mrr)} testid="root-stat-total-mrr" delay={0.35} />
        <StatCard icon={BadgeCheck} label="Active Accounts" value={s.accounts_active} testid="root-stat-active" delay={0.4} />
        <StatCard icon={AlarmClock} label="Expiring Soon" value={s.accounts_expiring} sub="5 days or less" testid="root-stat-expiring" delay={0.45} />
        <StatCard icon={CircleSlash} label="Expired Accounts" value={s.accounts_expired} sub="kept visible — data intact" testid="root-stat-expired" delay={0.5} />
        <StatCard icon={Ban} label="Suspended Accounts" value={s.accounts_suspended} testid="root-stat-suspended" delay={0.55} />
      </div>

      {s.role_counts && (
        <div className="mt-8 border border-border rounded-md p-6 bg-card" data-testid="role-health-table">
          <p className="overline-label mb-4">Account Health by Role</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="pb-2 font-bold">Account Type</th><th className="pb-2 font-bold">Total</th><th className="pb-2 font-bold">Active</th>
                  <th className="pb-2 font-bold">Expiring Soon</th><th className="pb-2 font-bold">Expired</th><th className="pb-2 font-bold">Suspended</th>
                </tr>
              </thead>
              <tbody>
                {[["Super Admins", "SUPER_ADMIN"], ["Partners", "PARTNER"], ["Business Owners", "BUSINESS_OWNER"]].map(([label, key]) => (
                  <tr key={key} className="border-b border-border/50" data-testid={`health-${key.toLowerCase()}`}>
                    <td className="py-2.5 font-bold">{label}</td>
                    <td className="py-2.5 font-semibold">{s.role_counts[key]?.total ?? 0}</td>
                    <td className="py-2.5 font-semibold text-emerald-600">{s.role_counts[key]?.active ?? 0}</td>
                    <td className="py-2.5 font-semibold text-amber-600">{s.role_counts[key]?.expiring ?? 0}</td>
                    <td className="py-2.5 font-semibold text-rose-500">{(s.role_counts[key]?.expired ?? 0) + (s.role_counts[key]?.renewal_pending ?? 0)}</td>
                    <td className="py-2.5 font-semibold text-rose-500">{s.role_counts[key]?.suspended ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-8 border border-border rounded-md p-6 bg-card max-w-3xl">
        <p className="overline-label mb-3">Hierarchy</p>
        <p className="text-sm text-muted-foreground leading-relaxed">
          ROOT SUPER ADMIN → Super Admins → Partners → Business Owners → Websites.
          This layer adds oversight and super-admin plans only — partner billing stays independent per partner,
          and everything below continues to work exactly as before.
        </p>
      </div>
    </div>
  );
}
